import {useState, useEffect} from 'react';
import {ThemeProvider} from '@mui/material';
import theme from '../../theme';
import InsuranceClaimsComponent from './component';
import {useInsuranceClaims, useInsuranceClaimsCounts} from './hooks';
import {useDebounce} from '~/modules/frontdesk/hooks';
import {PAGE_SIZE, ERROR_MESSAGES, InsuranceClaimsOrderBy} from './constants';

interface InsuranceClaimsContainerProps {
  filteredStatus?: string;
}

function InsuranceClaimsContainer({
  filteredStatus,
}: InsuranceClaimsContainerProps = {}) {
  const [searchText, setSearchText] = useState('');
  const debouncedSearchTerm = useDebounce<string>(searchText, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<
    InsuranceClaimsOrderBy | string | undefined
  >(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [status, setStatus] = useState<string | undefined>(filteredStatus);
  const [preservedTotalPages, setPreservedTotalPages] = useState(0);

  const params = {
    search: debouncedSearchTerm.trim(),
    ...(sortBy && {sortBy}),
    ...(sortBy && {sortDirection}),
    pageNumber: currentPage,
    perPage: PAGE_SIZE,
    ...(status && {status}),
  };

  const {data, isLoading, isError, error} = useInsuranceClaims(params);
  const {counts, isLoading: countsLoading} = useInsuranceClaimsCounts({
    search: debouncedSearchTerm.trim(),
  });

  const claims = data?.insuranceClaims || [];
  const totalPages = data?.totalPages || 0;
  const totalRecords = data?.totalRecords || 0;
  const message = data?.message || error?.message || ERROR_MESSAGES.fetchFailed;

  useEffect(() => {
    if (totalPages > 0) {
      setPreservedTotalPages(totalPages);
    }
  }, [totalPages]);

  const displayTotalPages = isLoading ? preservedTotalPages : totalPages;

  const allClaimsCount = counts.all_claims_count || 0;
  const newClaimCount = counts.new_claims_count || 0;
  const pendingCount = counts.pending_claims_count || 0;
  const submittedCount = counts.submitted_claims_count || 0;
  const rejectedCount = counts.rejected_claims_count || 0;
  const paidCount = counts.paid_claims_count || 0;
  const outstandingCount = counts.outstading_claims_count || 0;

  const handleNextPage = () => {
    const updatedPage = Math.min(currentPage + 1, displayTotalPages);
    setCurrentPage(updatedPage);
  };

  const handlePreviousPage = () => {
    const updatedPage = Math.max(currentPage - 1, 1);
    setCurrentPage(updatedPage);
  };

  const handleSearchTextChange = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleSortChange = (key: string, direction: 'asc' | 'desc') => {
    setSortBy(key);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handleStatusChange = (newStatus: string | undefined) => {
    setStatus(newStatus);
    setCurrentPage(1);
  };

  return (
    <ThemeProvider theme={theme}>
      <InsuranceClaimsComponent
        isLoading={isLoading || countsLoading}
        isError={isError}
        message={message}
        searchText={searchText}
        currentPage={currentPage}
        claims={claims}
        totalPages={displayTotalPages}
        totalRecords={totalRecords}
        sortBy={sortBy}
        sortDirection={sortDirection}
        status={status}
        onSearchTextChange={handleSearchTextChange}
        setCurrentPage={setCurrentPage}
        onSortChange={handleSortChange}
        onStatusChange={handleStatusChange}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
        allClaimsCount={allClaimsCount}
        newClaimCount={newClaimCount}
        pendingCount={pendingCount}
        submittedCount={submittedCount}
        rejectedCount={rejectedCount}
        paidCount={paidCount}
        outstandingCount={outstandingCount}
      />
    </ThemeProvider>
  );
}

export default InsuranceClaimsContainer;
